import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';

type ButtonVariant = 'primary' | 'secondary';
type ButtonSize = 'default' | 'small';

interface CTAButtonProps {
  to?: string;
  onClick?: () => void;
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  icon?: ReactNode;
}

/**
 * CTAButton Component
 * 
 * A reusable Call-to-Action button component that provides consistent styling
 * across the application with primary and secondary variants.
 * 
 * @param to - The route to navigate to (optional if onClick is provided)
 * @param onClick - Click handler function (optional if to is provided)
 * @param children - The button text or content
 * @param variant - 'primary' (solid amber) or 'secondary' (transparent with border)
 * @param className - Additional classes to apply
 * @param icon - Optional icon to display after the text
 */
export default function CTAButton({ 
  to, 
  onClick,
  children, 
  variant = 'primary',
  size = 'default',
  className = '',
  icon
}: CTAButtonProps) {
  // Base classes for all buttons
  const baseClasses = "font-semibold rounded-md transition-all duration-300 text-white flex items-center justify-center gap-2";
  
  // Size-specific classes
  const sizeClasses = {
    default: "text-base md:text-lg min-w-[200px] px-8 py-4",
    small: "text-sm px-5 py-2.5"
  };
  
  // Variant-specific classes
  const variantClasses = {
    primary: "bg-amber-500 hover:bg-amber-600 shadow-md hover:shadow-lg border-2 border-amber-300/80 hover:border-amber-300 relative overflow-hidden",
    secondary: "bg-gradient-to-b from-gray-300/40 via-gray-400/30 to-gray-600/40 backdrop-blur-sm hover:from-gray-200/50 hover:via-gray-300/40 hover:to-gray-500/50 border border-gray-300/60 hover:border-gray-200/80 shadow-[0_4px_12px_rgba(0,0,0,0.15)] hover:shadow-[0_6px_16px_rgba(0,0,0,0.2)] text-white hover:text-white relative overflow-hidden"
  };
  
  // Combine all classes
  const buttonClasses = `${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]} ${className}`;
  
  // Default right arrow icon if none provided
  const defaultIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
    </svg>
  );
  
  // Render button content
  const buttonContent = (
    <>
      {/* Add subtle shine effects for both button types */}
      {variant === 'secondary' && (
        <span className="absolute inset-0 overflow-hidden">
          <span className="absolute -rotate-12 -translate-x-full top-0 w-1/2 h-full bg-gradient-to-r from-transparent via-white/10 to-transparent transform animate-shine"></span>
        </span>
      )}
      {variant === 'primary' && (
        <span className="absolute inset-0 overflow-hidden">
          <span className="absolute -rotate-12 -translate-x-full top-0 w-1/3 h-full bg-gradient-to-r from-transparent via-amber-300/30 to-transparent transform animate-shine"></span>
        </span>
      )}
      {children}
      {icon || defaultIcon}
    </>
  );

  // Return either a Link or a button based on props
  return onClick ? (
    <button onClick={onClick} className={buttonClasses}>
      {buttonContent}
    </button>
  ) : (
    <Link to={to || '#'} className={buttonClasses}>
      {buttonContent}
    </Link>
  );
}
